import config from "../config";
import { HereAddressItem, HereAddressResponse } from "../types/address";
import { decodeFlexiblePolyline } from "../utils/flexiblePolyline";

/** A single lat/lng waypoint for road routing. */
export interface RouteWaypoint {
  lat: number;
  lng: number;
}

/** Result of a road-route request: the drawable path + trip totals. */
export interface CarRouteResult {
  /** Ordered `[lat, lng]` points that follow the road network. */
  path: Array<[number, number]>;
  distanceKm: number;
  durationMin: number;
}

/**
 * HERE Maps geocoding/search service.
 *
 * Uses the **Autosuggest** endpoint for the type-ahead list (better as-you-type
 * behaviour and richer POI / place-name matches than Discover), then enriches
 * the picked suggestion via the **Lookup** endpoint to recover the full
 * structured address (street / suburb / city / postcode), which Autosuggest
 * only returns as a combined label.
 *
 * Restricted to New Zealand via `in=countryCode:NZL` + a national focus point.
 *
 * NOTE: whether a specific named facility appears depends entirely on HERE's
 * POI coverage. e.g. "Poihipi Power Station" is not in HERE's dataset by that
 * name (HERE has the road + "Tuaropaki Station"); no query change surfaces a
 * name the provider doesn't store. Search by street address for those.
 */
class HereMapsService {
  private readonly autosuggestUrl =
    "https://autosuggest.search.hereapi.com/v1/autosuggest";
  private readonly lookupUrl = "https://lookup.search.hereapi.com/v1/lookup";
  private readonly routingUrl = "https://router.hereapi.com/v8/routes";
  // Restrict to NZ; bias ranking toward a neutral national focus point
  // (roughly Wellington / Cook Strait).
  private readonly countryRestriction = "countryCode:NZL";
  private readonly focusPoint = "-41.28,174.77";
  private readonly apiKey: string;

  constructor() {
    this.apiKey = config.hereMaps.API_KEY;

    if (!this.apiKey) {
      console.warn("HERE Maps API key is not configured. Please set VITE_HERE_API_KEY or REACT_APP_HERE_API_KEY in your environment variables.");
    } else if (process.env.NODE_ENV === 'development') {
      console.log("HERE Maps API key loaded:", this.apiKey.substring(0, 10) + "...");
    }
  }

  /** Shared HTTP + error handling for the HERE JSON endpoints. */
  private async fetchJson(url: string, signal?: AbortSignal): Promise<any> {
    const response = await fetch(url, {
      method: "GET",
      headers: { Accept: "application/json" },
      signal,
    });

    if (!response.ok) {
      let errorMessage = `HERE Maps API error: ${response.status} ${response.statusText}`;
      let errorDetails = "";
      try {
        const errorData = await response.json();
        if (errorData.title) errorMessage = errorData.title;
        if (errorData.action) errorDetails = errorData.action;
        if (errorData.cause) errorDetails += ` (${errorData.cause})`;
      } catch (e) {
        // Non-JSON error body — keep the default message.
      }

      if (response.status === 401) {
        throw new Error("Invalid HERE Maps API key. Please check your API credentials.");
      } else if (response.status === 429) {
        throw new Error("HERE Maps API rate limit exceeded. Please try again later.");
      } else if (response.status >= 500) {
        throw new Error("HERE Maps API server error. Please try again later.");
      }
      throw new Error(errorDetails ? `${errorMessage}. ${errorDetails}` : errorMessage);
    }

    return response.json();
  }

  /**
   * Type-ahead search — addresses AND named places (POIs) via Autosuggest.
   * Query-suggestion items (category / chain refinements) carry no coordinates
   * and are filtered out so only pickable addresses/places remain.
   *
   * @param query - Search query string (address or place name)
   * @param signal - Optional AbortSignal for request cancellation
   */
  async searchAddresses(
    query: string,
    signal?: AbortSignal
  ): Promise<HereAddressResponse> {
    if (!this.apiKey) {
      throw new Error("HERE Maps API key is not configured");
    }
    if (!query || query.trim().length === 0) {
      return { items: [] };
    }

    const params = new URLSearchParams({
      apikey: this.apiKey,
      q: query.trim(),
      at: this.focusPoint,
      in: this.countryRestriction,
      limit: "10",
    });
    const fullUrl = `${this.autosuggestUrl}?${params.toString()}`;

    if (process.env.NODE_ENV === 'development') {
      console.log('HERE Autosuggest call:', {
        url: fullUrl.replace(this.apiKey, 'API_KEY_HIDDEN'),
        query,
      });
    }

    try {
      const rawData = await this.fetchJson(fullUrl, signal);
      const items: HereAddressItem[] = (rawData.items || [])
        // Keep only results that resolve to a location (drop category/chain
        // query suggestions, which have no `position`).
        .filter((item: any) => item && item.position);

      if (process.env.NODE_ENV === 'development') {
        console.log('HERE Autosuggest response:', {
          query,
          itemsCount: items.length,
        });
      }

      return { items };
    } catch (error: any) {
      if (error.name === "AbortError") throw error;
      if (error instanceof Error) throw error;
      throw new Error("Failed to fetch addresses. Please check your internet connection.");
    }
  }

  /**
   * Resolve a suggestion `id` to a full record with the structured address
   * fields (street / district / city / state / postalCode / houseNumber).
   * Autosuggest returns only a combined `address.label`, so we call this on
   * selection to populate the individual address fields on the form.
   *
   * Returns `null` on any failure so the caller can fall back to the
   * suggestion's label rather than blocking the selection.
   */
  async lookupAddress(
    id: string,
    signal?: AbortSignal
  ): Promise<HereAddressItem | null> {
    if (!this.apiKey || !id) return null;

    const params = new URLSearchParams({ apikey: this.apiKey, id });
    const fullUrl = `${this.lookupUrl}?${params.toString()}`;

    try {
      const item = await this.fetchJson(fullUrl, signal);
      return item && item.id ? (item as HereAddressItem) : null;
    } catch (error: any) {
      if (error.name === "AbortError") throw error;
      // Non-abort failures are non-fatal — fall back to the label.
      if (process.env.NODE_ENV === 'development') {
        console.warn('HERE Lookup failed:', error);
      }
      return null;
    }
  }

  /**
   * Plan a car route THAT FOLLOWS THE ROADS through the given ordered waypoints
   * (first = origin, last = destination, the rest = `via` stops in order).
   *
   * Returns the concatenated road geometry (for drawing a real route line, not a
   * straight "as-the-crow-flies" segment) plus the total driving distance and
   * time. Returns `null` if there are fewer than two waypoints, the key is
   * missing, or the request fails — callers should degrade gracefully.
   */
  async getCarRoute(
    waypoints: RouteWaypoint[],
    signal?: AbortSignal
  ): Promise<CarRouteResult | null> {
    if (!this.apiKey) {
      console.warn("HERE Maps API key is not configured — cannot plan a road route.");
      return null;
    }
    const valid = waypoints.filter(
      (w) => Number.isFinite(w.lat) && Number.isFinite(w.lng)
    );
    if (valid.length < 2) return null;

    const origin = valid[0];
    const destination = valid[valid.length - 1];
    const vias = valid.slice(1, -1);

    const params = new URLSearchParams();
    params.set("apikey", this.apiKey);
    params.set("transportMode", "car");
    params.set("origin", `${origin.lat},${origin.lng}`);
    params.set("destination", `${destination.lat},${destination.lng}`);
    params.set("return", "polyline,summary");
    // `via` may repeat; keep the given order (route respects it as intermediate
    // stops rather than re-optimising them away).
    for (const v of vias) {
      params.append("via", `${v.lat},${v.lng}`);
    }

    const fullUrl = `${this.routingUrl}?${params.toString()}`;

    try {
      const data = await this.fetchJson(fullUrl, signal);
      const sections = data?.routes?.[0]?.sections;
      if (!Array.isArray(sections) || sections.length === 0) return null;

      const path: Array<[number, number]> = [];
      let meters = 0;
      let seconds = 0;
      for (const section of sections) {
        meters += section?.summary?.length ?? 0;
        seconds += section?.summary?.duration ?? 0;
        const decoded = decodeFlexiblePolyline(section?.polyline ?? "");
        // Drop the first point of every section after the first — it duplicates
        // the previous section's last point (the shared via stop).
        const start = path.length > 0 ? 1 : 0;
        for (let i = start; i < decoded.length; i += 1) path.push(decoded[i]);
      }
      if (path.length < 2) return null;

      return {
        path,
        distanceKm: meters / 1000,
        durationMin: seconds / 60,
      };
    } catch (error: any) {
      if (error?.name === "AbortError") throw error;
      if (process.env.NODE_ENV === "development") {
        console.warn("HERE routing failed:", error);
      }
      return null;
    }
  }
}

// Export singleton instance
export const hereMapsService = new HereMapsService();
